import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  BookOpen,
  Plus,
  Download,
  Edit,
  Trash2,
  Eye,
  Users,
  Clock,
  Settings,
  UserCheck,
  Calendar,
  GraduationCap,
} from "lucide-react";
import {
  getClasses,
  getClassStatistics,
  deleteClass,
  createClass,
  updateClass,
  getClass,
} from "../../services/classes";
import {
  ClassWithDetails,
  CreateClassRequest,
  UpdateClassRequest,
} from "../../types/class";
import ClassForm from "../../components/forms/ClassForm";
import ClassDetailsModal from "../../components/modals/ClassDetailsModal";
import StudentEnrollmentModal from "../../components/modals/StudentEnrollmentModal";
import TeacherAssignmentModal from "../../components/modals/TeacherAssignmentModal";
import ScheduleManagementModal from "../../components/modals/ScheduleManagementModal";
import SimpleClassModal from "../../components/modals/SimpleClassModal";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";
import Pagination from "../../components/common/Pagination";

const AdminClasses: React.FC = () => {
  const queryClient = useQueryClient();
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Handle Enter key press to trigger search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setActiveSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Modal states
  const [isClassModalOpen, setIsClassModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [editingClassId, setEditingClassId] = useState<string | null>(null);

  // Management modal states
  const [isManagementModalOpen, setIsManagementModalOpen] = useState(false);
  const [selectedClassForManagement, setSelectedClassForManagement] = useState<
    string | null
  >(null);
  const [isStudentEnrollmentModalOpen, setIsStudentEnrollmentModalOpen] =
    useState(false);
  const [isTeacherAssignmentModalOpen, setIsTeacherAssignmentModalOpen] =
    useState(false);
  const [isClassScheduleModalOpen, setIsClassScheduleModalOpen] =
    useState(false);

  // Fetch classes data
  const {
    data: classesResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "classes",
      currentPage,
      pageSize,
      activeSearchTerm,
      statusFilter,
      gradeFilter,
    ],
    queryFn: () =>
      getClasses({
        page: currentPage,
        limit: pageSize,
        search: activeSearchTerm,
        isActive:
          statusFilter === "all" ? undefined : statusFilter === "active",
        grade: gradeFilter === "all" ? undefined : gradeFilter,
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch class statistics
  const { data: statsResponse } = useQuery({
    queryKey: ["classStatistics"],
    queryFn: getClassStatistics,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Delete class mutation
  const deleteClassMutation = useMutation({
    mutationFn: deleteClass,
    onSuccess: () => {
      toast.success("Class deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["classes"] });
      queryClient.invalidateQueries({ queryKey: ["classStatistics"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to delete class");
    },
  });

  // Create class mutation
  const createClassMutation = useMutation({
    mutationFn: createClass,
    onSuccess: () => {
      toast.success("Class created successfully");
      queryClient.invalidateQueries({ queryKey: ["classes"] });
      queryClient.invalidateQueries({ queryKey: ["classStatistics"] });
      setIsClassModalOpen(false);
      setEditingClassId(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to create class");
    },
  });

  // Update class mutation
  const updateClassMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClassRequest }) =>
      updateClass(id, data),
    onSuccess: () => {
      toast.success("Class updated successfully");
      queryClient.invalidateQueries({ queryKey: ["classes"] });
      queryClient.invalidateQueries({ queryKey: ["classStatistics"] });
      setIsClassModalOpen(false);
      setEditingClassId(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to update class");
    },
  });

  const classes = classesResponse?.data || [];
  const pagination = classesResponse?.pagination;
  const stats = statsResponse?.data;

  // Handler functions
  const handleCreateClass = () => {
    setEditingClassId(null);
    setIsClassModalOpen(true);
  };

  const handleEditClass = (classId: string) => {
    setEditingClassId(classId);
    setIsClassModalOpen(true);
  };

  const handleDeleteClass = async (classId: string) => {
    if (window.confirm("Are you sure you want to delete this class?")) {
      deleteClassMutation.mutate(classId);
    }
  };

  const handleViewDetails = (classId: string) => {
    if (!classId) {
      toast.error("No class selected");
      return;
    }
    setSelectedClassId(classId);
    setIsDetailsModalOpen(true);
  };

  const handleClassSubmit = (classData: CreateClassRequest) => {
    if (editingClassId) {
      // Update existing class
      updateClassMutation.mutate({
        id: editingClassId,
        data: classData as UpdateClassRequest,
      });
    } else {
      // Create new class
      createClassMutation.mutate(classData);
    }
  };

  // Management functions
  const openManagementModal = (classId: string) => {
    setSelectedClassForManagement(classId);
    setIsManagementModalOpen(true);
  };

  const handleManageStudents = (classId: string) => {
    if (!classId) {
      toast.error("No class selected");
      return;
    }
    setSelectedClassId(classId);
    setIsStudentEnrollmentModalOpen(true);
    setIsManagementModalOpen(false);
  };

  const handleManageTeachers = (classId: string) => {
    if (!classId) {
      toast.error("No class selected");
      return;
    }
    setSelectedClassId(classId);
    setIsTeacherAssignmentModalOpen(true);
    setIsManagementModalOpen(false);
  };

  const handleManageSchedule = (classId: string) => {
    if (!classId) {
      toast.error("No class selected");
      return;
    }
    setSelectedClassId(classId);
    setIsClassScheduleModalOpen(true);
    setIsManagementModalOpen(false);
  };

  const handleExport = () => {
    // Implementation for exporting classes data
    console.log("Export classes data");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
        <div className="animate-pulse space-y-8">
          <div className="h-12 bg-white/40 backdrop-blur-sm rounded-2xl w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-32 bg-white/40 backdrop-blur-sm rounded-2xl"
              ></div>
            ))}
          </div>
          <div className="h-96 bg-white/40 backdrop-blur-sm rounded-2xl"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
        <div className="text-center py-20">
          <div className="bg-gradient-to-br from-red-400 to-red-500 w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <GraduationCap className="h-12 w-12 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            Error loading classes
          </h3>
          <p className="text-slate-600">
            {(error as any)?.message || "Something went wrong"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-8">
          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-gradient-to-br from-purple-400 to-indigo-500 p-3 rounded-2xl shadow-lg">
                  <BookOpen className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900">
                  Classes Management
                </h1>
              </div>
              <p className="text-slate-600 ml-[60px]">
                Manage classes and their assignments
              </p>
            </div>
            <button
              onClick={handleCreateClass}
              className="bg-white border-2 border-slate-200 hover:border-emerald-300 text-slate-700 px-6 py-3 rounded-xl font-bold flex items-center gap-3 hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <Plus className="h-5 w-5 text-emerald-500" />
              Add Class
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Total Classes
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {classes.length}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-400 to-indigo-500 p-4 rounded-2xl shadow-lg">
                <BookOpen className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Active Classes
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats?.totalClasses ||
                    classes.filter((c) => c.isActive).length}
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-400 to-green-500 p-4 rounded-2xl shadow-lg">
                <Users className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Total Students
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats?.totalStudents ||
                    classes.reduce((acc, c) => acc + (c.studentCount || 0), 0)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-400 to-indigo-500 p-4 rounded-2xl shadow-lg">
                <Users className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Avg. Class Size
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats?.averageClassSize ||
                    (classes.length > 0
                      ? Math.round(
                          classes.reduce(
                            (acc, c) => acc + (c.studentCount || 0),
                            0
                          ) / classes.length
                        )
                      : 0)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-4 rounded-2xl shadow-lg">
                <Clock className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-1 items-center gap-4 w-full">
              <div className="relative flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search classes... (Press Enter)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="w-full pl-4 pr-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-transparent font-medium text-slate-900 placeholder-slate-400"
                />
              </div>
              <select
                className="bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl px-4 py-3 font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                value={gradeFilter}
                onChange={(e) => {
                  setGradeFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Grades</option>
                {/* Dynamic grade options based on available classes */}
                {[...new Set(classes.map((c) => c.grade))].map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(
                    e.target.value as "all" | "active" | "inactive"
                  );
                  setCurrentPage(1);
                }}
                className="bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl px-4 py-3 font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <button
              onClick={handleExport}
              className="bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 px-6 py-3 rounded-xl font-bold flex items-center gap-3 hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <Download className="h-5 w-5 text-slate-500" />
              Export
            </button>
          </div>
        </div>

        {/* Classes Table */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
          <div ref={tableScrollRef} className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Class
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Class Teacher
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Students
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Room
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {classes.map((cls: ClassWithDetails) => (
                  <tr
                    key={cls._id}
                    className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 transition-all duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12">
                          <div className="h-12 w-12 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                            <BookOpen className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-bold text-slate-900">
                            {cls.name}
                          </div>
                          <div className="text-sm text-slate-600 font-medium">
                            {cls.section
                              ? `${cls.grade} - ${cls.section}`
                              : cls.grade}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">
                        {cls.teacherName || "No teacher assigned"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-slate-900 mb-1">
                        {cls.studentCount} / {cls.capacity}
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-blue-400 to-indigo-500 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${
                              (cls.studentCount / cls.capacity) * 100
                            }%`,
                          }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                      {cls.scheduleCount > 0
                        ? `${cls.scheduleCount} period${
                            cls.scheduleCount !== 1 ? "s" : ""
                          }`
                        : "No schedule"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                      {new Date(cls.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-3 py-1 text-xs font-bold rounded-xl ${
                          cls.isActive
                            ? "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white"
                            : "bg-gradient-to-br from-red-300 to-red-400 text-white"
                        }`}
                      >
                        {cls.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetails(cls._id)}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-all duration-200"
                          title="View Details"
                        >
                          <Eye className="h-5 w-5 text-blue-500" />
                        </button>
                        <button
                          onClick={() => handleEditClass(cls._id)}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-all duration-200"
                          title="Edit"
                          disabled={deleteClassMutation.isPending}
                        >
                          <Edit className="h-5 w-5 text-amber-500" />
                        </button>
                        <button
                          onClick={() => handleDeleteClass(cls._id)}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-all duration-200"
                          title="Delete"
                          disabled={deleteClassMutation.isPending}
                        >
                          <Trash2 className="h-5 w-5 text-red-500" />
                        </button>

                        {/* Management Button */}
                        <button
                          onClick={() => openManagementModal(cls._id)}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-all duration-200"
                          title="Manage Class"
                        >
                          <Settings className="h-5 w-5 text-purple-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {classes.length === 0 && (
            <div className="text-center py-20">
              <div className="bg-gradient-to-br from-slate-300 to-slate-400 w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <BookOpen className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                No classes found
              </h3>
              <p className="text-slate-600">
                Try adjusting your search criteria or add a new class.
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              onPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}

        {/* Management Actions Modal */}
        {isManagementModalOpen && selectedClassForManagement && (
          <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity z-[9998]"
                onClick={() => setIsManagementModalOpen(false)}
              ></div>

              <div className="relative inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full z-[10000]">
                <div className="bg-white/95 backdrop-blur-xl px-6 pt-6 pb-4 sm:p-8 sm:pb-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center rounded-2xl bg-gradient-to-br from-purple-400 to-indigo-500 sm:mx-0 h-12 w-12 shadow-lg">
                      <Settings className="h-6 w-6 text-white" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-xl font-bold text-slate-900 mb-1">
                        Manage Class
                      </h3>
                      <div className="mt-4">
                        <p className="text-sm font-medium text-slate-600 mb-4">
                          Choose an action to perform on this class:
                        </p>

                        <div className="space-y-3">
                          <button
                            onClick={() => {
                              if (selectedClassForManagement) {
                                handleManageStudents(
                                  selectedClassForManagement
                                );
                              }
                            }}
                            className="w-full flex items-center gap-4 px-5 py-4 text-left bg-white border-2 border-slate-200 hover:border-blue-300 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102"
                          >
                            <Users className="h-6 w-6 text-blue-500" />
                            <div>
                              <div className="font-bold text-slate-900">
                                Manage Students
                              </div>
                              <div className="text-xs text-slate-600 font-medium">
                                Enroll or remove students from class
                              </div>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              if (selectedClassForManagement) {
                                handleManageTeachers(
                                  selectedClassForManagement
                                );
                              }
                            }}
                            className="w-full flex items-center gap-4 px-5 py-4 text-left bg-white border-2 border-slate-200 hover:border-emerald-300 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102"
                          >
                            <UserCheck className="h-6 w-6 text-emerald-500" />
                            <div>
                              <div className="font-bold text-slate-900">
                                Assign Teachers
                              </div>
                              <div className="text-xs text-slate-600 font-medium">
                                Assign teachers to subjects
                              </div>
                            </div>
                          </button>

                          {/* <button
                            onClick={() => {
                              if (selectedClassForManagement) {
                                handleManageSchedule(selectedClassForManagement);
                              }
                            }}
                            className="w-full flex items-center gap-4 px-5 py-4 text-left bg-white border-2 border-slate-200 hover:border-purple-300 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102"
                          >
                            <Calendar className="h-6 w-6 text-purple-500" />
                            <div>
                              <div className="font-bold text-slate-900">Manage Schedule</div>
                              <div className="text-xs text-slate-600 font-medium">
                                Set up class timetable
                              </div>
                            </div>
                          </button> */}

                          <button
                            onClick={() => {
                              if (selectedClassForManagement) {
                                handleViewDetails(selectedClassForManagement);
                              }
                            }}
                            className="w-full flex items-center gap-4 px-5 py-4 text-left bg-white border-2 border-slate-200 hover:border-slate-300 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102"
                          >
                            <Eye className="h-6 w-6 text-slate-500" />
                            <div>
                              <div className="font-bold text-slate-900">
                                View Details
                              </div>
                              <div className="text-xs text-slate-600 font-medium">
                                View complete class information
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-4 sm:px-8 sm:flex sm:flex-row-reverse">
                  <button
                    onClick={() => setIsManagementModalOpen(false)}
                    className="w-full inline-flex justify-center rounded-xl border-2 border-slate-200 shadow-md px-6 py-3 bg-white font-bold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-400 sm:ml-3 sm:w-auto hover:scale-105 transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        <SimpleClassModal
          isOpen={isClassModalOpen}
          onClose={() => setIsClassModalOpen(false)}
          classId={editingClassId || undefined}
        />

        <ClassDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          classId={selectedClassId || ""}
        />

        <StudentEnrollmentModal
          isOpen={isStudentEnrollmentModalOpen}
          onClose={() => setIsStudentEnrollmentModalOpen(false)}
          classId={selectedClassId || ""}
        />

        <TeacherAssignmentModal
          isOpen={isTeacherAssignmentModalOpen}
          onClose={() => setIsTeacherAssignmentModalOpen(false)}
          classId={selectedClassId || ""}
        />

        <ScheduleManagementModal
          isOpen={isClassScheduleModalOpen}
          onClose={() => setIsClassScheduleModalOpen(false)}
          classId={selectedClassId || ""}
        />
      </div>
    </div>
  );
};

export default AdminClasses;
